"use client";

import React, { useEffect, useState, useMemo } from "react";
import ElasticCard from "./ElasticCard";
import Card from "./CareerCard";
import { jobOpenings } from "@/data/data";
import ApplyForm from "./ApplyForm";
import { useGLTF } from "@react-three/drei";


const CareerHero = () => {
  const [showElasticCard, setShowElasticCard] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState<string>("Developer");

  // List of all possible GLBs
  const cards = useMemo(
    () => [
      "/models/card.glb",
      "/models/QAcard.glb",
      "/models/artistCard.glb",
    ],
    []
  );

  // Dynamically update the first card based on position
  const cardGLB = useMemo(() => {
    if (position === "Developer") return "/models/card.glb";
    if (position === "QA") return "/models/QAcard.glb";
    if (position === "Artist") return "/models/artistCard.glb";
    return "/models/card.glb"; // fallback
  }, [position]);

  // Preload all GLB files once
  useEffect(() => {
    cards.forEach((path) => useGLTF.preload(path));
  }, [cards]);

  // Show ElasticCard only on large screens
  useEffect(() => {
    const checkSize = () => setShowElasticCard(window.innerWidth >= 1024);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return (
    <div className="overflow-hidden">
      <div className="w-full relative min-h-[calc(100vh-80px)]">
        {/* Elastic 3D Card */}
        <div className="absolute inset-0">
          <div className="z-10 lg:-ml-[700px] xl:-ml-[800px]">
            {showElasticCard && (
              <ElasticCard
                position={[0, 0, 20]}
                gravity={[0, -40, 0]}
                cardGLB={cardGLB}
              />
            )}
          </div>
        </div>
        {/* Glow background */}
        <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full 
          bg-yellow-500/20 blur-[160px] -z-10" />

        {/* Page Content */}
        <div className="sm:px-16 px-4 py-10 max-w-[1490px] w-full h-full mx-auto relative pointer-events-none">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="col-span-1">
              <p className="text-3xl md:text-4xl text-yellow-500 font-bold mb-4 sm:mb-8">
                Evonix Ventures
              </p>
              <p className="mb-4 sm:mb-8 text-left text-3xl font-semibold md:text-5xl">
                Start your adventure <br /> today.
              </p>
              <p className="text-xl">
                If you’re excited about the prospect of working with us and meet
                the qualifications below, please apply to join our team.
              </p>
            </div>

            {/* Job Cards */}
            <div className="pointer-events-auto">
              <div className="overflow-hidden">
                <h1 className="sectionTitle reveal-item">Available Positions</h1>
                <div className="space-y-10">
                  {jobOpenings.map((e, id) => (
                    <Card
                      key={id}
                      requirements={e.requirements}
                      title={e.title}
                      goodToHave={e.goodToHave}
                      isActive={activeIndex === id}
                      onClick={() => {
                        setActiveIndex(activeIndex === id ? -1 : id);
                        setPosition(e.position);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Application Form */}
      <div className="w-full h-full relative">
        <ApplyForm position={activeIndex} />
      </div>
    </div>
  );
};

export default CareerHero;
